import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MCError, MCText, Spinner, View } from '../basic';
import { api, lang, remCss } from 'src/helpers';
import LANG_CONST from 'src/helpers/lang/const';
import mainModel from './model';
import { GroupedVirtuoso, Virtuoso } from 'react-virtuoso';
import { SlotItem } from './SlotItem';
import errorDescriptions from 'src/constants/errors';

const LIMIT = 100;

export const SlotList = ({
  doctorId,
  startDate,
  endDate,
  tab,
  onHide,
  onSlots,
  group = true,
  slot,
  onBooked
}) => {
  const [slots, setSlots] = useState([]);
  const [invalid, setInvalid] = useState({});
  const [loading, setLoading] = useState(false);
  const [openEditId, setOpenEditId] = useState(null);
  const [openButtonId, setOpenButtonId] = useState(null);
  const [scrollItemId, setScrollItemId] = useState(null);
  const dataRef = useRef({ init: false });

  const scrollTo = ({ slot }) => {
    setSlots((o) => {
      const n = o.map((i) => (i.id === slot.id ? { ...i, focusTime: Date.now() } : i));
      let index = n.findIndex((i) => i.id === slot.id);
      setScrollItemId(index >= 0 ? index : null);
      return n;
    });
  };

  const loadList = useCallback(() => {
    if (loading) return;
    setLoading(true);
    setInvalid({});
    api('schedule/list', {
      from: startDate,
      to: endDate,
      id: doctorId,
      freeOnly: tab === 'free',
      bookedOnly: tab === 'booked',
      skip: 0,
      limit: LIMIT
    }).then(({ error, data }) => {
      if (!error) {
        setSlots(data);
      } else {
        setInvalid({ error: errorDescriptions(error) });
      }
      setLoading(false);

      if (!dataRef.current.init && slot) {
        dataRef.current.init = true;
        setScrollItemId(data.findIndex((i) => i.id === slot.id));
        setSlots(data.map((i) => (i.id === slot.id ? { ...i, focusTime: Date.now() } : i)));
      }
    });
  }, [startDate, endDate, tab, doctorId, loading, slot]);

  const loadMore = useCallback(() => {
    if (loading || !slots.length || slots.length % LIMIT !== 0) return;
    setLoading(true);
    setInvalid({});
    api('schedule/list', {
      from: startDate,
      to: endDate,
      id: doctorId,
      freeOnly: tab === 'free',
      bookedOnly: tab === 'booked',
      skip: slots.length,
      limit: LIMIT
    }).then(({ error, data }) => {
      if (!error) {
        setSlots((o) => [...o, ...data]);
      } else {
        setInvalid({ error: errorDescriptions(error) });
      }
      setLoading(false);
    });
  }, [startDate, endDate, slots, tab, loading, doctorId]);

  const onItem = useCallback(
    ({ slot, isNew = false }) => {
      if (!isNew)
        setSlots((o) => {
          return o.map((item) => {
            if (item.id === slot?.id) {
              return {
                ...item,
                ...slot
              };
            }
            return item;
          });
        });
      else if (slots.length < LIMIT) {
        setSlots((o) => {
          const n = [...o, { ...slot, focusTime: Date.now() }].sort(
            (a, b) => a.startDate - b.startDate
          );
          let index = n.findIndex((i) => i.id === slot.id);
          setScrollItemId(index >= 0 ? index : null);
          return n;
        });
      } else {
        loadList();
      }
    },
    [slots, loadList]
  );

  useEffect(() => {
    const onChangeParams = () => {
      setSlots([]);
      setOpenEditId(null);
      setOpenButtonId(null);
      loadList();
    };
    onChangeParams();
  }, [startDate, endDate, tab]);

  useEffect(() => {
    const onScroll = () => {
      if (scrollItemId) {
        setScrollItemId(null);
        if (dataRef.current.listEl)
          setTimeout(() => {
            dataRef.current.listEl.scrollIntoView({
              index: scrollItemId,
              behavior: 'auto', // Smooth scrolling animation
              align: 'center'
            });
          }, 500);
      }
    };
    onScroll();
  }, [scrollItemId]);

  useEffect(() => {
    if (onSlots) onSlots(slots);
  }, [slots, onSlots]);

  useEffect(() => {
    mainModel.on(`slot_schedule_slot_list_refresh`, loadList);
    mainModel.on(`slot_schedule_slot_list_item`, onItem);
    mainModel.on(`slot_generate_slot_list_scroll_item`, scrollTo);
    if (tab) {
      mainModel.on(`slot_schedule_slot_list_${tab}_refresh`, loadList);
      mainModel.on(`slot_schedule_slot_list_${tab}_item`, onItem);
    }
    return () => {
      mainModel.cancel(`slot_schedule_slot_list_refresh`, loadList);
      mainModel.cancel(`slot_schedule_slot_list_item`, onItem);
      mainModel.cancel(`slot_generate_slot_list_scroll_item`, scrollTo);
      if (tab) {
        mainModel.cancel(`slot_schedule_slot_list_${tab}_refresh`, loadList);
        mainModel.cancel(`slot_schedule_slot_list_${tab}_item`, onItem);
      }
    };
  }, [startDate, endDate, tab, slots]);

  const { groupedDays, groupCounts, flatSlots } = useMemo(() => {
    if (!group) return { groupedDays: [], groupCounts: [], flatSlots: slots };
    const groups = {};

    slots.forEach((slot) => {
      // If startDate is in minutes, multiply by 60000 to get milliseconds
      const dateObj = new Date(slot.startDate);
      const dayKey = dateObj.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      if (!groups[dayKey]) groups[dayKey] = [];
      groups[dayKey].push(slot);
    });

    const dayKeys = Object.keys(groups);
    const counts = dayKeys.map((key) => groups[key].length);
    const flattened = dayKeys.flatMap((key) => groups[key]);

    return {
      groupedDays: dayKeys, // Array of Day Strings
      groupCounts: counts, // Array of item counts per day
      flatSlots: flattened // Flattened slots aligned with counts
    };
  }, [slots, group]);

  if (!group)
    return (
      <View style={{ height: '100%' }}>
        <Virtuoso
          style={{ flex: 1 }}
          data={slots}
          ref={(r) => (dataRef.current.listEl = r)}
          endReached={loadMore}
          components={{
            Header: () =>
              invalid.error && (
                <div style={{ paddingBottom: 20 }}>
                  <MCError error={invalid.error} />
                </div>
              ),
            Footer: () => (
              <div
                style={{
                  textAlign: 'center',
                  paddingBottom: 20,
                  paddingTop: slots.length ? 0 : 80,
                  flex: 1
                }}
              >
                {loading ? (
                  <Spinner />
                ) : !slots.length ? (
                  lang.t(LANG_CONST.text.no_results_yet)
                ) : (
                  ''
                )}
              </div>
            )
          }}
          itemContent={(index, slot) => {
            return (
              <div className={`mc-pb10 ${index === 0 ? 'mc-pt10' : ''}`}>
                <SlotItem
                  slot={slot}
                  slots={slots}
                  openEditId={openEditId}
                  setOpenEditId={setOpenEditId}
                  onDeleted={(err) => {
                    if (!err) {
                      setSlots((o) =>
                        o.map((s) => (s.id !== slot.id ? s : { ...s, deleted: true }))
                      );
                    }
                  }}
                  onToggled={(err, data) => {
                    if (data?.ok) onItem({ slot: data.data });
                  }}
                  onSaved={(err, data) => {
                    if (data?.ok) onItem({ slot: data.data });
                  }}
                  onBooked={loadList}
                  onCancelled={loadList}
                  onRescheduled={loadList}
                  onHide={onHide}
                  openButtonId={openButtonId}
                  setOpenButtonId={setOpenButtonId}
                  setScrollItemId={(v) => {
                    setScrollItemId(v ? index : null);
                  }}
                />
              </div>
            );
          }}
        />
      </View>
    );

  return (
    <View style={{ height: '100%' }}>
      <GroupedVirtuoso
        style={{ flex: 1 }}
        groupCounts={groupCounts}
        ref={(r) => (dataRef.current.listEl = r)}
        endReached={loadMore}
        components={{
          Header: () =>
            invalid.error && (
              <div style={{ paddingBottom: 20 }}>
                <MCError error={invalid.error} />
              </div>
            ),
          Footer: () => (
            <div
              style={{
                textAlign: 'center',
                paddingBottom: 20,
                paddingTop: slots.length ? 0 : 80,
                flex: 1
              }}
            >
              {loading ? <Spinner /> : !slots.length ? lang.t(LANG_CONST.text.no_results_yet) : ''}
            </div>
          )
        }}
        // Sticky Day Header
        groupContent={(index) => {
          const dayLabel = groupedDays[index];
          return (
            <MCText
              label={dayLabel}
              size={20}
              weight={500}
              textLight
              style={{
                backgroundColor: 'white',
                textAlign: 'right',
                padding: remCss(20)
              }}
            />
          );
        }}
        itemContent={(index) => {
          const slot = flatSlots[index];
          return (
            <div className={`mc-pb10 ${index === 0 ? 'mc-pt10' : ''}`} key={slot.id}>
              <SlotItem
                slot={slot}
                openEditId={openEditId}
                setOpenEditId={setOpenEditId}
                onDeleted={(err) => {
                  if (!err) {
                    mainModel.emit('slot_schedule_slot_list_item', {
                      slot: { ...slot, deleted: true }
                    });
                  }
                }}
                onToggled={(err, data) => {
                  if (data?.ok)
                    mainModel.emit('slot_schedule_slot_list_item', {
                      slot: data.data
                    });
                }}
                onSaved={(err, data) => {
                  if (data?.ok)
                    mainModel.emit('slot_schedule_slot_list_item', {
                      slot: data.data
                    });
                }}
                onBooked={() => {
                  setTimeout(() => mainModel.emit('slot_schedule_slot_list_refresh'), 100);
                  onBooked && onBooked();
                }}
                onCancelled={() =>
                  setTimeout(() => mainModel.emit('slot_schedule_slot_list_refresh'), 100)
                }
                onRescheduled={() =>
                  setTimeout(() => mainModel.emit('slot_schedule_slot_list_refresh'), 100)
                }
                onHide={onHide}
                openButtonId={openButtonId}
                slots={slots}
                setOpenButtonId={setOpenButtonId}
                setScrollItemId={(v) => {
                  setScrollItemId(v ? index : null);
                }}
              />
            </div>
          );
        }}
      />
    </View>
  );
};
